import React, { useEffect, useMemo } from 'react';
import FlowBaseInfo from '@src/emp-3.0/components/Metrics/Flow/FlowForm/FlowBaseInfo';
import FlowEvents from '@src/emp-3.0/components/Metrics/Flow/FlowForm/FlowEvents';
import FlowSettings from '@src/emp-3.0/components/Metrics/Flow/FlowForm/FlowSettings';
import FlowNetworkRequestPatterns from 'emp-3.0/components/Metrics/Flow/FlowForm/FlowNetworkRequestPatterns';
import SqlTextBox from '@src/emp-3.0/components/Metrics/Flow/FlowForm/SqlTextBox';
import { Form, Collapse, Tag } from 'antd';
import { cloneDeep } from 'lodash';
import { IFlowFormProps } from './typed';
import {
  CollapseTitle,
  CustomCollapse,
  Dot,
  FlowNameWrapper,
  LabelWrapper,
  DescriptionWrapper,
} from '@src/emp-3.0/components/Metrics/Flow/FlowForm/styled';
import FlowErrorEvents from '@src/emp-3.0/components/Metrics/Flow/FlowForm/FlowErrorEvents';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';
import { isViewMode, useIsPatternMode } from '@src/pages/metric-templates/utils';
import { RightOutlined } from '@ant-design/icons';
import { useBuildFlowFormData } from '@src/emp-3.0/hooks';
import { useGetFlowLabels, useGetJourneyStages } from '@src/emp-3.0/api/queryHooks';

const getCollapseHeader = ({ key, mode, isPatternMode }) => {
  const isView = isViewMode(mode);

  const labels = {
    '0': isPatternMode ? 'Pattern Information' : 'Flow Information',
    '1': isView
      ? isPatternMode
        ? 'Pattern Definition'
        : 'Flow Definition'
      : isPatternMode
      ? 'Define Your Pattern'
      : 'Define Your Flow',
    '2': 'Set Experience and Performance Settings',
    '3': 'Additional Settings',
  };

  const label = labels?.[key];

  return (
    <LabelWrapper>
      {isView ? (
        <>
          <Dot /> {label}
        </>
      ) : (
        `${key}. ${label}`
      )}
    </LabelWrapper>
  );
};

const FlowForm: React.FC<IFlowFormProps> = ({ flow, mode, formProps, resetErrorField }) => {
  const [internalFormInstance] = Form.useForm();
  const { buildFlowFormData } = useBuildFlowFormData();
  const {
    setActiveSectionKey,
    activeSectionKey,
    initialEvent,
    completeEvent,
    withoutEvent,
    setInitialEvent,
    setCompleteEvent,
    setWithoutEvent,
    errorEvents,
    setFormInstance,
  } = useFlowStore();

  const { data: flowLabelsData = [] } = useGetFlowLabels();
  // const { data: journeyStagesData = [] } = useGetJourneyStages();

  const form = formProps?.form || internalFormInstance;
  const initialFormValues = buildFlowFormData();
  const isView = isViewMode(mode);
  const { isPatternMode } = useIsPatternMode();

  // Compute names directly from flow data
  // const journeyStage = useMemo(() => {
  //   if (!flow?.journey_stage) return '';
  //   const stage = journeyStagesData.find((item: any) => item.id === flow.journey_stage);
  //   return stage?.journey_stage || '';
  // }, [flow?.journey_stage, journeyStagesData]);

  const labels = useMemo(() => {
    if (!Array.isArray(flow?.labels)) return [];
    return flow.labels
      .map((id) => {
        const label = flowLabelsData.find((item: any) => item.id === id);
        return label?.label;
      })
      .filter(Boolean);
  }, [flow?.labels, flowLabelsData]);

  useEffect(() => {
    setFormInstance(form);
  }, [form, setFormInstance]);

  useEffect(() => {
    form.resetFields();
    if (flow) {
      const copied = cloneDeep(flow);
      if (typeof copied.definition.events.start_event === 'object') {
        setInitialEvent(cloneDeep(copied.definition.events.start_event));
        copied.definition.events.start_event = null;
      }
      if (typeof copied.definition.events.success_event === 'object') {
        setCompleteEvent(cloneDeep(copied.definition.events.success_event));
        copied.definition.events.success_event = null;
      }
      // Handle without_event like other events
      if (
        typeof copied.definition?.events?.without_event === 'object' &&
        copied.definition.events.without_event !== null
      ) {
        setWithoutEvent(cloneDeep(copied.definition.events.without_event));
        copied.definition.events.without_event = null;
      }
      form.setFieldsValue({ ...copied });
    }
  }, [flow, form, setInitialEvent, setCompleteEvent, setWithoutEvent]);
  const initialEventId = Form.useWatch(['definition', 'events', 'start_event'], form);
  const completeEventId = Form.useWatch(['definition', 'events', 'success_event'], form);
  const flowSqlDefinition = Form.useWatch(['definition', 'flow_sql_definition'], form);
  const hasTwoEvents = !!(initialEventId && completeEventId);
  const propagativeDimensions = Form.useWatch(['definition', 'propagative_dimensions'], form);
  const CollapseItems = useMemo(() => {
    const descriptionCollapse = [
      {
        key: '0',
        header: (
          <CollapseTitle>
            {getCollapseHeader({ key: '0', mode: mode, isPatternMode })}
          </CollapseTitle>
        ),
        children: (
          <DescriptionWrapper>
            {flow?.description && <div className="section-title">Description</div>}
            <div>{flow?.description}</div>
            {labels?.length > 0 && (
              <>
                <div className="section-title">Labels</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', rowGap: '4px' }}>
                  {labels?.map((labelName) => (
                    <Tag key={labelName} color="green">
                      {labelName}
                    </Tag>
                  ))}
                </div>
              </>
            )}
          </DescriptionWrapper>
        ),
      },
    ];
    const eventsCollapse = {
      key: '1',
      header: (
        <CollapseTitle>{getCollapseHeader({ key: '1', mode: mode, isPatternMode })}</CollapseTitle>
      ),
      children: isPatternMode ? (
        <SqlTextBox isView={isView} form={form} />
      ) : (
        <FlowEvents form={form} isView={isView} />
      ),
    };
    const experienceCollapse = {
      key: '2',
      header: (
        <CollapseTitle disabled={isPatternMode ? !flowSqlDefinition : !hasTwoEvents}>
          {getCollapseHeader({ key: '2', mode: mode, isPatternMode })}
        </CollapseTitle>
      ),
      children: (
        <>
          <FlowErrorEvents form={form} isView={isView} />
          <div style={{ marginLeft: '28px' }}>
            <FlowNetworkRequestPatterns mode={mode} form={form} />
          </div>
        </>
      ),
    };
    const settingsCollapse = {
      key: '3',
      header: (
        <CollapseTitle disabled={isPatternMode ? !flowSqlDefinition : !hasTwoEvents}>
          {getCollapseHeader({ key: isPatternMode ? '2' : '3', mode: mode, isPatternMode })}
        </CollapseTitle>
      ),
      children: (
        <div style={{ marginBottom: '25px' }}>
          <FlowSettings isView={isView} />
        </div>
      ),
    };
    const collapseItems: any = [];

    // add this description section only for view mode
    if (isView && (flow?.description || labels?.length > 0)) {
      collapseItems.push(...descriptionCollapse);
    }
    collapseItems.push(eventsCollapse);
    // remove experience section for pattern mode
    if (!isPatternMode) {
      collapseItems.push(experienceCollapse);
    }
    collapseItems.push(settingsCollapse);
    return collapseItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasTwoEvents,
    mode,
    isView,
    flow,
    form,
    isPatternMode,
    initialEvent,
    completeEvent,
    withoutEvent,
    errorEvents,
    labels,
    flowSqlDefinition,
    propagativeDimensions,
  ]);

  const handleFormValuesChange = (changedValues, values) => {
    // some internal logic to handle form values change

    const timeoutChangedValue = changedValues?.definition?.settings?.timeout;
    const timeoutUnitChangedValue = changedValues?.definition?.settings?.timeout_unit;
    const distributionMin = values.definition?.settings?.distribution?.min;
    const timeoutUnit = values.definition?.settings?.timeout_unit;
    const isChangeTimeout = timeoutChangedValue !== undefined;
    const isChangeTimeoutUnit = timeoutUnitChangedValue !== undefined;

    // If the user manually enters a timeout value, automatically set the distribution max as timeout * 2.
    if (isChangeTimeout || isChangeTimeoutUnit) {
      let max = values?.definition?.settings?.timeout * 2 || undefined;
      // distribution max is 1 hour because in data model 1.0 the max timeout is 1 hour.
      if (timeoutUnit === 'seconds') {
        max = max > 60 * 60 ? 60 * 60 : max;
      }
      if (timeoutUnit === 'minutes') {
        max = max > 60 ? 60 : max;
      }
      const definition =
        distributionMin === undefined
          ? {
              definition: {
                settings: { distribution: { min: isChangeTimeout ? 0 : undefined, max } },
              },
            }
          : {
              definition: { settings: { distribution: { max } } },
            };
      form.setFieldsValue(definition);
      resetErrorField?.(definition);
    }

    formProps?.onValuesChange?.(changedValues, values);
  };

  return (
    <Form
      form={form}
      initialValues={initialFormValues}
      preserve={false}
      {...formProps}
      name={'flowForm'}
      onValuesChange={handleFormValuesChange}
    >
      {isView ? null : (
        <FlowNameWrapper>
          <FlowBaseInfo />
        </FlowNameWrapper>
      )}
      <div>
        <CustomCollapse
          expandIconPosition={'end'}
          expandIcon={({ isActive }) => (
            <RightOutlined rotate={isActive ? 90 : 0} style={{ color: 'rgba(0,0,0,0.45)' }} />
          )}
          bordered={false}
          ghost
          activeKey={activeSectionKey}
          onChange={(key) => setActiveSectionKey(key)}
        >
          {CollapseItems.map((item) => {
            const { key, children, header, ...otherProps } = item;
            return (
              <Collapse.Panel
                key={key}
                header={header}
                style={{ borderBottom: '1px solid #D9D9D9' }}
                forceRender
                {...otherProps}
              >
                {children}
              </Collapse.Panel>
            );
          })}
        </CustomCollapse>
      </div>
    </Form>
  );
};

export default FlowForm;
